import { Link as RouterLink } from '@tanstack/react-router';
import { deriveObjectivePlan, type ObjectiveListItem, type ObjectivePlan } from '@ddp/shared';

import { useT } from '../../i18n/index.js';
import { useObjectivesList } from '../objectives/use-objectives-list.js';
import { STEP_LABEL, TASK_LABEL } from '../objectives/labels.js';

/**
 * Liste des **objectifs en cours** de l'organisation (ADR-0028) : tous les projets, chacun avec son
 * objectif et un aperçu de son plan guidé (avancement + point de reprise). Chaque carte mène au détail
 * du projet, où l'on définit/modifie l'objectif. Le plan est dérivé ici, jamais stocké.
 *
 * `withHeading` : sous le bloc processus « Créer un modèle », on coiffe la liste d'un titre et on
 * **s'efface totalement** quand il n'y a aucun objectif — le CTA de démarrage juste au-dessus tient
 * déjà lieu d'invite, un message « aucun projet » ferait doublon.
 */
export function ObjectivesSection({ withHeading = false }: { withHeading?: boolean } = {}) {
  const t = useT();
  const { items, loading } = useObjectivesList();

  if (loading) return null;
  if (items.length === 0) {
    return withHeading ? null : <p className="ddp-videos__empty">{t('model.objectives.empty')}</p>;
  }

  return (
    <section className="ddp-obj-list">
      {withHeading && <h2 className="ddp-models__subtitle">{t('model.objectives.ongoing')}</h2>}
      <p className="ddp-obj-list__hint">{t('model.objectives.hint')}</p>
      <ul className="ddp-obj-list__grid">
        {items.map((item) => (
          <ObjectiveCard key={item.projectId} item={item} />
        ))}
      </ul>
    </section>
  );
}

function ObjectiveCard({ item }: { item: ObjectiveListItem }) {
  const t = useT();
  const plan: ObjectivePlan | null = item.objective
    ? deriveObjectivePlan(
        {
          task: item.objective.task,
          targets: item.objective.targets,
          dataOnHand: item.objective.dataOnHand,
        },
        item.signals,
      )
    : null;

  return (
    <li className="ddp-obj-list__card">
      <RouterLink
        to="/projects/$projectId"
        params={{ projectId: item.projectId }}
        className="ddp-obj-list__name"
        title={item.projectName}
      >
        {item.projectName}
      </RouterLink>

      {item.objective && plan ? (
        <>
          <div className="ddp-obj-list__meta">
            <span className="ddp-obj-list__task">{t(TASK_LABEL[item.objective.task])}</span>
            {item.objective.targets.length > 0 && (
              <span className="ddp-obj-list__targets" title={item.objective.targets.join(', ')}>
                {item.objective.targets.join(', ')}
              </span>
            )}
          </div>
          <Progress plan={plan} />
        </>
      ) : (
        <span className="ddp-obj-list__none">{t('model.objectives.none')}</span>
      )}
    </li>
  );
}

/** Avancement compact : ratio d'étapes ouvertes faites + point de reprise (ou « complété »). */
function Progress({ plan }: { plan: ObjectivePlan }) {
  const t = useT();
  const open = plan.steps.filter((s) => s.state !== 'locked');
  const doneOpen = open.filter((s) => s.state === 'done').length;
  const pct = open.length ? Math.round((doneOpen / open.length) * 100) : 0;

  return (
    <div className="ddp-obj-list__progress">
      <div className="ddp-obj-list__progress-row">
        <span>
          {plan.resumeStepId
            ? `${t('model.objectives.resume')} · ${t(STEP_LABEL[plan.resumeStepId] ?? 'objective.title')}`
            : t('model.objectives.completed')}
        </span>
        <span>
          {doneOpen}/{open.length}
        </span>
      </div>
      <div className="ddp-obj-list__progress-bar">
        <div className="ddp-obj-list__progress-fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
